import React from 'react';
import * as LucideIcons from 'lucide-react';
import { journeySteps } from '../../data/mock';
import useReveal from '../../hooks/useReveal';

/**
 * The learning loop: practise → unlock → clear → certify.
 *
 * Laid out as a vertical timeline with a connecting rule, because the order and
 * the gate between steps IS the product argument - the 80% threshold is what
 * makes the final score worth anything.
 */
const HowItWorksSection = () => {
  const ref = useReveal({ stagger: 120 });

  return (
    <section ref={ref} className="section">
      <header className="section-header">
        <p className="eyebrow reveal">How it works</p>
        <h2 className="heading-2 reveal mt-3">A score you have to earn</h2>
        <p className="body-large reveal">
          You cannot skip ahead. Each stage opens only when the last one is genuinely
          done - which is exactly why the result means something.
        </p>
      </header>

      <ol className="relative mx-auto max-w-[820px]">
        {/* Connecting line behind the markers. */}
        <span
          aria-hidden="true"
          className="absolute left-[27px] top-4 bottom-4 hidden w-px sm:block"
          style={{
            background:
              'linear-gradient(180deg, transparent, var(--border-medium) 8%, var(--border-medium) 92%, transparent)'
          }}
        />

        {journeySteps.map((step, i) => {
          const Icon = LucideIcons[step.icon] || LucideIcons.Circle;
          const isLast = i === journeySteps.length - 1;

          return (
            <li key={step.step} className="reveal relative flex gap-5 pb-10 last:pb-0 sm:gap-7">
              {/* Marker */}
              <span
                className="relative z-10 grid h-14 w-14 shrink-0 place-items-center rounded-2xl"
                style={{
                  background: isLast
                    ? 'linear-gradient(135deg, var(--tenses-blue), var(--tenses-blue-light))'
                    : '#fff',
                  border: isLast ? 'none' : '1px solid var(--border-medium)',
                  color: isLast ? '#fff' : 'var(--tenses-blue)',
                  boxShadow: isLast ? 'var(--shadow-blue)' : 'var(--shadow-sm)'
                }}
              >
                <Icon className="h-5 w-5" />
              </span>

              {/* Copy */}
              <div className="pt-1.5">
                <div className="flex items-baseline gap-3">
                  <span
                    className="display text-[0.85rem] tabular-nums"
                    style={{ color: 'var(--text-muted)' }}
                  >
                    {step.step}
                  </span>
                  <h3 className="heading-3">{step.title}</h3>
                </div>
                <p className="body-medium mt-2 max-w-[560px] text-[0.95rem]">
                  {step.description}
                </p>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
};

export default HowItWorksSection;
